/**
 * Formatacao compartilhada pelas telas.
 *
 * `dia()` existia em copia em `Decisao.tsx`, e ia nascer de novo em
 * `ListaLicitacoes`, `AnaliseHumana` e `PainelDetalhe` — quatro definicoes do
 * mesmo formato, que divergem na primeira vez que uma delas muda. Pior: o
 * cartao mostrava `encerramento.slice(0, 10)`, ou seja AAAA-MM-DD cru, ao lado
 * de datas em DD/MM/AAAA.
 */
import type { LicitacaoResumo } from "./api/cliente";


/**
 * DD/MM/AAAA HH:MM — para instante em que a HORA decide alguma coisa.
 *
 * Encerramento de proposta e abertura de sessao sao os dois casos: perder por
 * ter lido "22/09" e chegado as 16h e um jeito caro de economizar cinco
 * caracteres. O resto da tela continua com `dia()`, que e o certo onde a hora
 * nao muda nada.
 *
 * Convertido para o fuso de quem le, e nao impresso como veio: o banco guarda
 * um INSTANTE (`2027-04-07T03:00:00Z` e meia-noite de Brasilia), e mostrar o
 * texto cru trocaria a data do prazo por um dia inteiro.
 */
export function dataHora(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** DD/MM/AAAA, ou string vazia quando nao ha data ou ela nao e valida. */
export function dia(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("pt-BR");
}

/**
 * O VALOR EM POUCAS PALAVRAS, para o cartão: "R$ 1,18 mi", "R$ 250 mil".
 *
 * O servidor manda o valor por extenso ("R$ 1.180.000,00", `moeda`), que é o
 * certo na ficha. No cartão ele é o dado que mais decide — o piso é de R$ 250
 * mil — e tem de ser lido de relance, e não decifrado dígito a dígito. Devolve
 * nulo quando não há valor ("valor não informado" é dito por quem chama), e o
 * texto como veio quando não dá para ler o número: nunca inventa um.
 */
export function valorCurto(valor: string | null | undefined): string | null {
  if (!valor) return null;
  const n = Number(valor.replace(/[^0-9,]/g, "").replace(",", "."));
  if (!Number.isFinite(n)) return valor;
  if (n >= 1e6) {
    return `R$ ${(n / 1e6).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} mi`;
  }
  if (n >= 1e3) {
    return `R$ ${(n / 1e3).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} mil`;
  }
  return valor;
}

/**
 * QUANTO FALTA PARA O PRAZO, em palavras e em estado — a mesma conta por dia
 * de calendário do painel (uma sessão amanhã às 9h é "amanhã", e não "hoje"
 * porque faltam 19 horas). `nivel` diz a cor: vencido, perto (até 7 dias) e
 * normal; sem data, `semData`.
 */
export type NivelDoPrazo = "vencido" | "perto" | "normal" | "semData";

export function prazoEm(iso: string | null | undefined): {
  texto: string;
  nivel: NivelDoPrazo;
} {
  if (!iso) return { texto: "sem prazo", nivel: "semData" };
  const alvo = new Date(iso);
  if (Number.isNaN(alvo.getTime())) return { texto: "sem prazo", nivel: "semData" };
  const hoje = new Date();
  const faltam = Math.round(
    (new Date(alvo.getFullYear(), alvo.getMonth(), alvo.getDate()).getTime() -
      new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()).getTime()) / 864e5,
  );
  if (faltam < 0) return { texto: "encerrada", nivel: "vencido" };
  if (faltam === 0) return { texto: "hoje", nivel: "perto" };
  if (faltam === 1) return { texto: "amanhã", nivel: "perto" };
  return { texto: `em ${faltam} dias`, nivel: faltam <= 7 ? "perto" : "normal" };
}

/** DD/MM, para onde o ano é óbvio e o espaço é pouco. */
export function diaMes(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

/**
 * O TIPO DE SERVIÇO da oportunidade, para o cartão e o detalhe (pedido de
 * 19/10).
 *
 * Vem dos tipos de atividade (`tipos`, os mesmos que o cruzamento usa para
 * escolher os atestados), do principal ao acessório. A licitação ainda não
 * classificada, ou cujo objeto não é nenhuma das 30 atividades, cai na
 * categoria que a triagem deu; e sem nenhuma das duas a tela DIZ que não há
 * tipo, em vez de deixar o espaço vazio — vazio se lê "não há o que dizer".
 */
export function tiposDeServico(
  i: { tipos?: string[] | null; categoria?: string | null },
  max = 2,
): { nomes: string[]; classificado: boolean } {
  if (i.tipos?.length) return { nomes: i.tipos.slice(0, max), classificado: true };
  if (i.categoria) {
    // "fora do escopo" é a triagem dizendo que NÃO é serviço do Nectar: aparece
    // como tipo, mas apagado — não é uma atividade.
    const foraDoEscopo = i.categoria.trim().toLowerCase() === "fora do escopo";
    // A categoria da triagem vem sem acento ("capacitacao e formacao"): a
    // tela a mostra com a grafia certa.
    const nome = i.categoria.trim().toLowerCase().split(/\s+/)
      .map((w) => (w === "mpe" ? "MPE" : acentua(w))).join(" ");
    return {
      nomes: [maiuscula(nome)],
      classificado: !foraDoEscopo,
    };
  }
  return { nomes: ["Tipo ainda não classificado"], classificado: false };
}

/**
 * A SITUAÇÃO DA OPORTUNIDADE, em duas palavras: onde ela está no funil. Cor
 * forte é decisão de PESSOA (aprovada, verde); a regra que a pôs em Avaliar é
 * âmbar; o resto é apagado. Segue a mesma hierarquia das abas: a decisão de
 * uma pessoa vem antes de tudo, depois a etapa da disputa, depois a faixa.
 */
export function situacaoDe(
  i: Pick<LicitacaoResumo, "decisao" | "etapa" | "faixa">,
): { texto: string; tom: "ok" | "atencao" | "nao" | "neutro" } | null {
  if (i.decisao === "aprovada") {
    const etapa: Record<string, string> = {
      preparacao: "Em preparação",
      em_disputa: "Em disputa",
      vencida: "Vencida",
      perdida: "Perdida",
    };
    return {
      texto: (i.etapa && etapa[i.etapa]) || "Aprovada",
      tom: i.etapa === "perdida" ? "nao" : "ok",
    };
  }
  if (i.decisao === "descartada") return { texto: "Reprovada", tom: "nao" };
  if (i.faixa === "avaliar") return { texto: "Para avaliar", tom: "atencao" };
  if (i.faixa === "aguardando") return { texto: "Aguardando leitura", tom: "neutro" };
  if (i.faixa === "passar") return { texto: "Descartada", tom: "neutro" };
  return null;
}


/* ── O TEXTO DA FONTE, LEGÍVEL (28/10) ─────────────────────────────────────
 *
 * "Deixe o texto capitalizado e mais amigável sempre onde for parte que o
 * usuário vai passar mais tempo lendo." Medido no banco local: 79% dos órgãos
 * vêm em CAIXA ALTA e sem acento ("MUNICIPIO DE CARACOL"), 25% dos objetos
 * inteiros em caixa alta, e muitos outros com trechos gritados no meio ("tem
 * por objeto a CONTRATAÇÃO DE EMPRESA ESPECIALIZADA…"); o município às vezes
 * vem todo em minúsculas ("boca da mata").
 *
 * Só a EXIBIÇÃO muda: o dado no banco, a busca, o PDF e a evidência literal
 * (que é conferida palavra por palavra contra o texto da fonte) continuam com
 * o texto como veio. As siglas ficam em maiúsculas — as da lista, as que não
 * têm vogal (FMS, CNPJ), as com número (HER2) e os algarismos romanos —, e a
 * UF só quando vem colada a "/", "-" ou "(" ("Camboriú/SC", "Pombos-PE"):
 * solta, "SE" é o pronome. */

const UFS = new Set(["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG",
  "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"]);

const SIGLAS = new Set([
  "SEBRAE", "SENAI", "SENAC", "SESC", "SESI", "SENAR", "SENAT", "SEST", "SESCOOP", "SEDUC", "SEMED",
  "SUS", "AEE", "EJA", "MEI", "EPP", "LTDA", "EIRELI", "TI", "TIC", "PNCP", "BNB", "BNDES",
  "BID", "IBGE", "ABNT", "ISO", "EAD", "CRAS", "CREAS", "CAPS", "UBS", "ESF", "ART", "CAT", "CREA",
  "CAU", "CRA", "CRC", "NR", "LGPD", "ERP", "BPM", "ODS", "PPA", "LDO", "LOA", "PEI", "APAE", "SRP",
  "EMBRAPA", "SANEPAR", "SABESP", "COMPESA", "CAGECE", "EMBASA", "CASAN", "COPASA", "CEF", "FNDE",
  "PNAE", "PDDE", "SAAE", "DETRAN", "DER", "DNIT", "INSS", "SIGTAP", "GED", "BIM", "CAD", "GPS",
  "SIG", "SAAS", "EMEI", "EMEF", "CMEI", "CEI", "RH", "SST", "UPA", "UTI", "SAMU", "PPP",
  "ONG", "OSC", "OSCIP", "FGTS", "TCU", "TCE", "CGU", "PGE", "MPE", "MPF", "TJ", "TRT", "TRE", "TRF",
  "STJ", "STF", "ANVISA", "ANATEL", "ANEEL", "INCRA", "IBAMA", "ICMBIO", "FUNASA", "FIOCRUZ",
  "EBSERH", "IPML", "IPTU", "ISS", "ICMS", "PIB", "MEC", "UNESCO", "ONU", "OMS", "OPAS", "PNUD",
  "UNICEF", "IFCE", "IFPE", "IFPI", "UFPE", "UFC", "UFPI", "UFRJ", "UFMG", "USP", "UNICAMP", "UNB",
  "CNPJ", "CPF", "SEFAZ", "PRODEB", "PRODAM", "SERPRO", "DATAPREV", "CELEPAR", "CICAN",
]);

/** As palavras que ficam minúsculas no meio de um nome. */
const PARTICULAS = new Set(["de", "da", "do", "das", "dos", "e", "em", "na", "no", "nas", "nos", "a",
  "o", "as", "os", "para", "por", "com", "à", "ao", "aos", "sem", "sob"]);

/** A grafia de quem escreveu em caixa alta sem acento — só palavras que
 *  aparecem nos nomes dos órgãos e nas categorias, e nenhuma que mude o
 *  sentido sem o acento ("para", "e" e "sao" ficam de fora). */
const ACENTOS: Record<string, string> = {
  municipio: "município", municipios: "municípios", saude: "saúde", educacao: "educação",
  assistencia: "assistência", previdencia: "previdência", camara: "câmara", publica: "pública",
  publico: "público", publicas: "públicas", publicos: "públicos", servico: "serviço",
  servicos: "serviços", informacao: "informação", administracao: "administração", gestao: "gestão",
  economico: "econômico", economica: "econômica", ciencia: "ciência", tecnico: "técnico",
  tecnica: "técnica", tecnologico: "tecnológico", agencia: "agência", agua: "água",
  transito: "trânsito", policia: "polícia", justica: "justiça", seguranca: "segurança",
  habitacao: "habitação", regiao: "região", consorcio: "consórcio", fundacao: "fundação",
  instituicao: "instituição", associacao: "associação", comunicacao: "comunicação",
  eletrica: "elétrica", hidricos: "hídricos", agricola: "agrícola", juridico: "jurídico",
  uniao: "união", brasilia: "brasília", goias: "goiás", amapa: "amapá", ceara: "ceará",
  parana: "paraná", maranhao: "maranhão", piaui: "piauí", espirito: "espírito",
  rondonia: "rondônia", paraiba: "paraíba", formacao: "formação", capacitacao: "capacitação",
  selecao: "seleção", certificacao: "certificação", residuos: "resíduos", solidos: "sólidos",
  telessaude: "telessaúde", promocao: "promoção", producao: "produção", inovacao: "inovação",
  logistica: "logística", orcamento: "orçamento", industria: "indústria", comercio: "comércio",
  aguas: "águas", ministerio: "ministério", licitacao: "licitação", licitacoes: "licitações",
  superintendencia: "superintendência", tecnologia: "tecnologia", ensino: "ensino",
};

/** A letra sem acento e em minúscula, uma por uma: o texto "de base" tem o
 *  MESMO comprimento do original, e o que se acha num se troca no outro. */
const base = (t: string) =>
  [...t].map((c) => (c.normalize("NFD")[0] ?? c).toLocaleLowerCase("pt-BR")).join("");

/**
 * OS NOMES QUE O REGISTRO JÁ TEM CERTOS voltam com a grafia deles: o
 * município vem com acento no campo próprio ("Moraújo") e sem acento no nome
 * do órgão e no objeto em caixa alta ("MORAUJO"). A troca é por palavra
 * inteira, sem olhar acento nem caixa.
 */
function restaura(t: string, nomes: (string | null | undefined)[]): string {
  let out = t;
  for (const n of nomes) {
    if (!n || n.length < 3) continue;
    const alvo = base(n);
    const b = base(out);
    let i = b.indexOf(alvo);
    while (i >= 0) {
      const antes = b[i - 1] ?? " ";
      const depois = b[i + alvo.length] ?? " ";
      if (!/[\p{L}\p{N}]/u.test(antes) && !/[\p{L}\p{N}]/u.test(depois)) {
        out = out.slice(0, i) + n + out.slice(i + n.length);
      }
      i = b.indexOf(alvo, i + alvo.length);
    }
  }
  return out;
}

const ROMANO = /^(?:I{2,3}|IV|VI{0,3}|IX|XI{0,3}|X)$/;
const VOGAL = /[aeiouyáéíóúâêôãõàAEIOUYÁÉÍÓÚÂÊÔÃÕÀ]/;
const LETRA = /\p{L}/u;

/** A palavra que fica como está (em maiúsculas): sigla, sem vogal, com
 *  número, romano. `antes` e `depois` são os caracteres em volta dela. */
function eSigla(p: string, antes: string, depois: string): boolean {
  if (p !== p.toLocaleUpperCase("pt-BR")) return false; // só o que já veio em maiúsculas
  if (/\d/.test(p)) return true;
  if (UFS.has(p)) return /[/\-(]/.test(antes) || depois === ")" || depois === "/";
  if (SIGLAS.has(p)) return true;
  if (p.length >= 2 && LETRA.test(p) && !VOGAL.test(p)) return true;
  return ROMANO.test(p);
}

const acentua = (w: string) => ACENTOS[w] ?? w;
const maiuscula = (w: string) => w.charAt(0).toLocaleUpperCase("pt-BR") + w.slice(1);
const proporcaoDeMaiusculas = (s: string) => {
  const letras = s.match(/\p{L}/gu) ?? [];
  const altas = letras.filter((c) => c !== c.toLocaleLowerCase("pt-BR")).length;
  return letras.length ? altas / letras.length : 0;
};

/**
 * O NOME (órgão, município) legível: "MUNICIPIO DE CARACOL" → "Município de
 * Caracol"; "boca da mata" → "Boca da Mata". O nome que já vem com maiúsculas
 * e minúsculas misturadas é de quem o escreveu, e fica como veio.
 */
export function nomeLegivel(s: string | null | undefined, nomes: (string | null | undefined)[] = []): string {
  if (!s) return "";
  const t = s.replace(/\s+/g, " ").trim();
  const prop = proporcaoDeMaiusculas(t);
  if (prop > 0 && prop < 0.6) return t;
  let primeira = true;
  return restaura(t.replace(/[\p{L}\p{N}]+/gu, (p: string, i: number) => {
    const sigla = prop > 0 && eSigla(p, t[i - 1] ?? "", t[i + p.length] ?? "");
    if (sigla) { primeira = false; return p; }
    const baixa = acentua(p.toLocaleLowerCase("pt-BR"));
    const r = !primeira && PARTICULAS.has(baixa) ? baixa : maiuscula(baixa);
    primeira = false;
    return r;
  }), nomes);
}

/**
 * A FRASE (o objeto) legível: tira a etiqueta do sistema de compras na frente
 * ("[Portal de Compras Públicas] - "), junta as quebras de linha e passa a
 * caixa alta — a frase inteira, ou o trecho gritado no meio dela — para caixa
 * de frase, com a primeira letra de cada frase maiúscula.
 */
export function fraseLegivel(s: string | null | undefined, nomes: (string | null | undefined)[] = []): string {
  if (!s) return "";
  const t = s.replace(/\s+/g, " ").trim().replace(/^\[[^\]]{2,60}\]\s*[-–—:]\s*/, "");
  const tudo = proporcaoDeMaiusculas(t) > 0.6;
  const palavras = [...t.matchAll(/[\p{L}\p{N}]+/gu)].map((m) => {
    const p = m[0];
    const i = m.index ?? 0;
    return {
      p,
      i,
      alta: LETRA.test(p) && p === p.toLocaleUpperCase("pt-BR") && p !== p.toLocaleLowerCase("pt-BR"),
      sigla: eSigla(p, t[i - 1] ?? "", t[i + p.length] ?? ""),
    };
  });
  let out = "";
  let fim = 0;
  palavras.forEach((w, k) => {
    out += t.slice(fim, w.i);
    fim = w.i + w.p.length;
    // No texto misto, só o TRECHO gritado baixa (a palavra em caixa alta com
    // outra em caixa alta ao lado); a palavra solta em maiúsculas é sigla.
    const trecho = !!(palavras[k - 1]?.alta || palavras[k + 1]?.alta);
    const baixa = w.alta && !w.sigla && (tudo || trecho);
    // A palavra colada a uma UF é o nome de uma cidade ("SORRISO/MT").
    const cidade = !!palavras[k + 1]?.sigla && UFS.has(palavras[k + 1]!.p)
      && /^[/-]$/.test(t.slice(fim, palavras[k + 1]!.i));
    const minuscula = acentua(w.p.toLocaleLowerCase("pt-BR"));
    out += baixa ? (cidade ? maiuscula(minuscula) : minuscula) : w.p;
  });
  out += t.slice(fim);
  // A primeira letra do texto e a de cada frase.
  return restaura(out, nomes).replace(/(^|[.!?]\s+)(\p{Ll})/gu, (_: string, a: string, c: string) =>
    a + c.toLocaleUpperCase("pt-BR"));
}
